import Phaser from "phaser";
import type { TextDirection } from "@yjsgames/i18n";
import { mirrorX } from "./mirror.js";
import { clampLevel, levelFromLocalX, segmentFilled } from "./levelBar.js";

/**
 * `SegmentLevelBar` 값 변경 이벤트 키.
 * - `Change`: 클릭/드래그로 레벨이 **변경될 때만** 새 레벨(정수)을 인자로 emit (같은 값 반복 emit 안 함).
 */
export const SEGMENT_LEVEL_BAR_EVENT = {
  Change: "segmentLevelBar:change",
} as const;

/** `SegmentLevelBar` 스타일 토큰 — 색만 호출자 주입(DIP). 칸 크기는 width/height 옵션. */
export interface LevelBarStyleToken {
  /** 채워진 칸 색(0xRRGGBB). */
  readonly filledColor: number;
  /** 빈 칸 색. */
  readonly emptyColor: number;
  /** 칸 사이 간격(px). default 2. */
  readonly gap?: number;
  /** 빈 칸 알파. default 1. */
  readonly emptyAlpha?: number;
}

export interface SegmentLevelBarOptions {
  readonly style: LevelBarStyleToken;
  /** 바 전체 가로 폭 — maxLevel 칸으로 균등 분할(gap 포함). */
  readonly width: number;
  readonly height: number;
  /** 최대 레벨 = 칸 수. default 10. */
  readonly maxLevel?: number;
  /** 초기 레벨(0~maxLevel). */
  readonly level: number;
  /** 텍스트 방향 — RTL이면 칸 순서·포인터 매핑·채움 방향 미러. default "ltr"(회귀 0). */
  readonly dir?: TextDirection;
}

/**
 * N칸 세그먼트 레벨 바 — 값 0~maxLevel(=칸 수). 채워진 칸 수 = 값.
 *
 * 클릭/드래그(pointermove + isDown)로 포인터 위치 비례 레벨을 설정한다.
 * `dir:"rtl"`이면 칸 위치(`mirrorX`)·포인터→레벨 매핑·채움 방향이 모두 좌우 반전된다.
 * 좌표 로직은 `levelBar.ts` 순수 함수(`levelFromLocalX`/`segmentFilled`)에 위임 — 테스트로 커버.
 *
 * 도메인 무관(볼륨·밝기 등 0~N 단계 선택). 호출자는 `style`(색)만 주입하고
 * 값 변경은 `SEGMENT_LEVEL_BAR_EVENT.Change`로 구독, 외부 동기화는 `setLevel`로 한다.
 */
export class SegmentLevelBar extends Phaser.GameObjects.Container {
  private readonly segments: Phaser.GameObjects.Rectangle[] = [];
  private readonly style: LevelBarStyleToken;
  private readonly maxLevel: number;
  private readonly barWidth: number;
  private readonly segWidth: number;
  private readonly gap: number;
  private dir: TextDirection;
  private level: number;

  constructor(scene: Phaser.Scene, x: number, y: number, options: SegmentLevelBarOptions) {
    super(scene, x, y);
    this.style = options.style;
    this.maxLevel = options.maxLevel ?? 10;
    this.barWidth = options.width;
    this.dir = options.dir ?? "ltr";
    this.level = clampLevel(options.level, this.maxLevel);
    this.gap = options.style.gap ?? 2;
    this.segWidth = (options.width - this.gap * (this.maxLevel - 1)) / this.maxLevel;

    for (let i = 0; i < this.maxLevel; i++) {
      const sx = this.segmentX(i);
      const rect = scene.add
        .rectangle(sx, 0, this.segWidth, options.height, this.style.filledColor)
        .setOrigin(0, 0);
      this.segments.push(rect);
      this.add(rect);
    }

    // 투명 히트 영역(전 폭) — 클릭/드래그 입력 수신. origin(0,0)이라 콜백 localX = 0~width.
    const hit = scene.add
      .rectangle(0, 0, options.width, options.height, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    this.add(hit);
    hit.on("pointerdown", (_p: Phaser.Input.Pointer, localX: number) => {
      this.applyPointer(localX);
    });
    hit.on("pointermove", (pointer: Phaser.Input.Pointer, localX: number) => {
      if (pointer.isDown) this.applyPointer(localX);
    });

    this.applyFill();
  }

  /** 외부에서 레벨 강제 설정 (저장 복원 등). Change 이벤트는 emit하지 않는다. */
  setLevel(level: number): void {
    this.setLevelInternal(clampLevel(level, this.maxLevel), false);
  }

  /** 현재 레벨(0~maxLevel). */
  getLevel(): number {
    return this.level;
  }

  /** 방향 전환 — 칸 위치 재배치 + 채움 재계산. (보통 언어 변경은 scene.restart라 드물게 사용.) */
  setDirection(dir: TextDirection): void {
    if (this.dir === dir) return;
    this.dir = dir;
    this.segments.forEach((seg, i) => seg.setX(this.segmentX(i)));
    this.applyFill();
  }

  /** i번째 칸의 좌측 x — LTR 좌→우 누적, RTL은 mirrorX로 반전. */
  private segmentX(idx: number): number {
    return mirrorX(idx * (this.segWidth + this.gap), this.segWidth, this.barWidth, this.dir);
  }

  private applyPointer(localX: number): void {
    this.setLevelInternal(levelFromLocalX(localX, this.barWidth, this.maxLevel, this.dir), true);
  }

  private setLevelInternal(level: number, emit: boolean): void {
    if (level === this.level) return; // 변경 없으면 재렌더·emit 생략
    this.level = level;
    this.applyFill();
    if (emit) this.emit(SEGMENT_LEVEL_BAR_EVENT.Change, level);
  }

  private applyFill(): void {
    const emptyAlpha = this.style.emptyAlpha ?? 1;
    for (let i = 0; i < this.segments.length; i++) {
      const filled = segmentFilled(i, this.level, this.maxLevel, this.dir);
      const seg = this.segments[i];
      if (filled) seg.setFillStyle(this.style.filledColor, 1);
      else seg.setFillStyle(this.style.emptyColor, emptyAlpha);
    }
  }
}
